import { keymap } from '@codemirror/view';
import { Prec } from '@codemirror/state';
import { indentMore, indentLess } from '@codemirror/commands';
import { completionStatus } from '@codemirror/autocomplete';
import { get } from 'svelte/store';
import { claimsSettings } from '../../stores/settingsStore';

// Test that settings are accessible
console.log('[ClaimNumbering] Module loaded, testing settings access:', {
    claimsSettings,
    currentValue: get(claimsSettings)
});

/**
 * Claim Auto-Numbering Extension for Patent Claims Editor
 * 
 * Features:
 * - Auto-increment claim numbers on Enter
 * - Two-level subnumbering: (a, b, c) and (i, ii, iii)
 * - Tab/Shift+Tab navigation between levels
 * - Auto-capitalize first letter after number
 * - Two-enter to exit (remove number on empty numbered line)
 * - Clean undo/redo support
 */

console.log('[ClaimNumbering] Extension loaded');

// Patterns to match different numbering levels
const CLAIM_NUMBER_PATTERN = /^(\d+)\.\s/;
const LETTER_PATTERN = /^(\s*)\(([a-z])\)\s/;
const ROMAN_PATTERN = /^(\s*)\(([ivxlcdm]+)\)\s/i;

// Roman numeral conversion utilities
const ROMAN_VALUES = [
    { value: 1000, numeral: 'm' },
    { value: 900, numeral: 'cm' },
    { value: 500, numeral: 'd' },
    { value: 400, numeral: 'cd' },
    { value: 100, numeral: 'c' },
    { value: 90, numeral: 'xc' },
    { value: 50, numeral: 'l' },
    { value: 40, numeral: 'xl' },
    { value: 10, numeral: 'x' },
    { value: 9, numeral: 'ix' },
    { value: 5, numeral: 'v' },
    { value: 4, numeral: 'iv' },
    { value: 1, numeral: 'i' }
];

function toRoman(num) {
    let result = '';
    let remaining = num;
    
    for (const { value, numeral } of ROMAN_VALUES) {
        while (remaining >= value) {
            result += numeral;
            remaining -= value;
        }
    }
    
    return result;
}

function fromRoman(str) {
    const romanToInt = { 'i': 1, 'v': 5, 'x': 10, 'l': 50, 'c': 100, 'd': 500, 'm': 1000 };
    let result = 0;
    const normalized = str.toLowerCase();
    
    for (let i = 0; i < normalized.length; i++) {
        const current = romanToInt[normalized[i]];
        const next = romanToInt[normalized[i + 1]];
        
        if (next && current < next) {
            result -= current;
        } else {
            result += current;
        }
    }
    
    return result;
}

/**
 * Detect the numbering level and info from a line
 */
function detectNumberingLevel(line) {
    const text = line.text;
    
    // Check for main claim number
    const claimMatch = text.match(CLAIM_NUMBER_PATTERN);
    if (claimMatch) {
        return {
            level: 0,
            type: 'number',
            value: parseInt(claimMatch[1]),
            indent: '',
            pattern: claimMatch[0]
        };
    }
    
    // Check indent level to determine if we should treat as roman vs letter
    // Second level (8+ spaces) should be roman numerals
    const indentLength = text.match(/^(\s*)/)[1].length;
    
    // For deeply indented items (8+ spaces), check for roman pattern first
    if (indentLength >= 8) {
        const romanMatch = text.match(ROMAN_PATTERN);
        if (romanMatch) {
            return {
                level: 2,
                type: 'roman',
                value: romanMatch[2].toLowerCase(),
                indent: romanMatch[1],
                pattern: romanMatch[0]
            };
        }
    }
    
    // Check for letter subnumber (first level indent, 4 spaces)
    const letterMatch = text.match(LETTER_PATTERN);
    if (letterMatch && indentLength < 8) {
        return {
            level: 1,
            type: 'letter',
            value: letterMatch[2],
            indent: letterMatch[1],
            pattern: letterMatch[0]
        };
    }
    
    // Fallback check for roman if not caught above
    const romanMatch = text.match(ROMAN_PATTERN);
    if (romanMatch) {
        return {
            level: 2,
            type: 'roman',
            value: romanMatch[2].toLowerCase(),
            indent: romanMatch[1],
            pattern: romanMatch[0]
        };
    }
    
    return null;
}

/**
 * Check if a line contains only a numbering pattern (empty content)
 */
function isEmptyNumberedLine(line) {
    const numberInfo = detectNumberingLevel(line);
    if (!numberInfo) return false;
    
    const text = line.text;
    const result = text.trim() === numberInfo.pattern.trim();
    
    console.log('[ClaimNumbering] isEmptyNumberedLine check:', {
        text,
        pattern: numberInfo.pattern,
        result
    });
    
    return result;
}

/**
 * Check if cursor is at the end of a numbered line with content
 */
function isAtEndOfNumberedLine(state, pos) {
    const line = state.doc.lineAt(pos);
    
    console.log('[ClaimNumbering] isAtEndOfNumberedLine check START:', {
        pos,
        lineFrom: line.from,
        lineTo: line.to,
        lineText: line.text,
        cursorAtEnd: pos === line.to
    });
    
    const numberInfo = detectNumberingLevel(line);
    if (!numberInfo) {
        console.log('[ClaimNumbering] No numbering detected');
        return false;
    }
    
    // Check if cursor is at end of line
    if (pos !== line.to) {
        console.log('[ClaimNumbering] Cursor not at end of line');
        return false;
    }
    
    // Check if line has content after the number
    if (line.text.trim() === numberInfo.pattern.trim()) {
        console.log('[ClaimNumbering] Line has no content after number');
        return false;
    }
    
    console.log('[ClaimNumbering] isAtEndOfNumberedLine PASSED - should insert next number');
    return true;
}

/**
 * Get the next value for a given numbering type
 */
function getNextValue(type, currentValue) {
    switch (type) {
        case 'number':
            return currentValue + 1;
        case 'letter':
            // Next letter in sequence
            return String.fromCharCode(currentValue.charCodeAt(0) + 1);
        case 'roman':
            // Convert to number, increment, convert back
            return toRoman(fromRoman(currentValue) + 1);
        default:
            return null;
    }
}

/**
 * Find the last number of a specific type in the document
 * Enhanced to handle undo/redo scenarios better
 */
function findLastNumber(state, type) {
    let lastValue = null;
    let values = [];
    
    for (let i = 1; i <= state.doc.lines; i++) {
        const line = state.doc.line(i);
        const numberInfo = detectNumberingLevel(line);
        
        if (numberInfo && numberInfo.type === type) {
            if (type === 'number') {
                values.push(numberInfo.value);
            } else {
                // For letters and romans, track all values seen
                values.push(numberInfo.value);
            }
        }
    }
    
    if (values.length === 0) {
        return null;
    }
    
    if (type === 'number') {
        // For main claims, find the highest number
        lastValue = Math.max(...values);
    } else {
        // For letters and romans, use the last one in document order
        // But also validate it makes sense in sequence
        lastValue = values[values.length - 1];
        
        // For letters, ensure we don't go beyond 'z'
        if (type === 'letter' && typeof lastValue === 'string') {
            if (lastValue >= 'z') {
                lastValue = 'z';
            }
        }
    }
    
    return lastValue;
}

/**
 * Find the next appropriate letter for a new subclaim
 * Looks at existing letters to determine the next in sequence, considering proper scoping
 */
function findNextLetter(state, currentLineNumber) {
    const letters = [];
    let previousMainClaimLine = null;
    
    // First, find the previous main claim before the current line
    for (let i = currentLineNumber - 1; i >= 1; i--) {
        const line = state.doc.line(i);
        const numberInfo = detectNumberingLevel(line);
        
        if (numberInfo && numberInfo.type === 'number') {
            previousMainClaimLine = i;
            break;
        }
    }
    
    // Collect letters from after the previous main claim up to current line
    const startLine = previousMainClaimLine ? previousMainClaimLine + 1 : 1;
    const endLine = currentLineNumber - 1;
    
    for (let i = startLine; i <= endLine; i++) {
        const line = state.doc.line(i);
        const numberInfo = detectNumberingLevel(line);
        
        if (numberInfo && numberInfo.type === 'letter') {
            letters.push(numberInfo.value);
        }
    }
    
    if (letters.length === 0) {
        return 'a'; // Start with 'a' if no letters exist in this section
    }
    
    // Find the highest letter in the alphabet sequence within this section
    const sortedLetters = letters.sort();
    const lastLetter = sortedLetters[sortedLetters.length - 1];
    
    // Return the next letter in sequence
    if (lastLetter < 'z') {
        return String.fromCharCode(lastLetter.charCodeAt(0) + 1);
    } else {
        return 'a'; // Wrap around if we've reached 'z'
    }
}

/**
 * Find the next appropriate roman numeral for a new sub-subclaim
 * Looks at existing roman numerals to determine the next in sequence, considering proper scoping
 */
function findNextRoman(state, currentLineNumber) {
    const romans = [];
    let previousLetterClaimLine = null;
    
    // First, find the previous letter claim before the current line
    for (let i = currentLineNumber - 1; i >= 1; i--) {
        const line = state.doc.line(i);
        const numberInfo = detectNumberingLevel(line);
        
        if (numberInfo && numberInfo.type === 'letter') {
            previousLetterClaimLine = i;
            break;
        }
    }
    
    // Collect romans from after the previous letter claim up to current line
    const startLine = previousLetterClaimLine ? previousLetterClaimLine + 1 : 1;
    const endLine = currentLineNumber - 1;
    
    for (let i = startLine; i <= endLine; i++) {
        const line = state.doc.line(i);
        const numberInfo = detectNumberingLevel(line);
        
        if (numberInfo && numberInfo.type === 'roman') {
            romans.push(numberInfo.value);
        }
    }
    
    if (romans.length === 0) {
        return 'i'; // Start with 'i' if no romans exist in this section
    }
    
    // Convert all romans to numbers, find max, then increment
    const romanNumbers = romans.map(fromRoman);
    const maxNumber = Math.max(...romanNumbers);
    
    // Return the next roman numeral
    return toRoman(maxNumber + 1);
}

/**
 * Remove the numbering from an empty numbered line
 */
function removeNumbering(view, line) {
    const numberInfo = detectNumberingLevel(line);
    if (!numberInfo) return false;
    
    const transaction = view.state.update({
        changes: {
            from: line.from,
            to: line.from + numberInfo.pattern.length,
            insert: numberInfo.indent // Keep indentation
        }
    });
    
    view.dispatch(transaction);
    return true;
}

/**
 * Insert the next number at the same level
 */
function insertNextNumber(view, pos) {
    const state = view.state;
    const currentLine = state.doc.lineAt(pos);
    const numberInfo = detectNumberingLevel(currentLine);
    
    console.log('[ClaimNumbering] insertNextNumber called:', {
        pos,
        currentLineText: currentLine.text,
        numberInfo
    });
    
    if (!numberInfo) {
        console.log('[ClaimNumbering] No number info found');
        return false;
    }
    
    // Check settings for subnumber auto-numbering
    const settings = get(claimsSettings);
    const autoNumberSubitems = settings.autoNumberSubitems;
    
    console.log('[ClaimNumbering] Settings check:', {
        settings,
        autoNumberSubitems,
        numberInfoType: numberInfo.type,
        isSubnumber: numberInfo.type === 'letter' || numberInfo.type === 'roman'
    });
    
    // If it's a subnumber and auto-numbering is disabled, just insert a newline with proper indent
    if ((numberInfo.type === 'letter' || numberInfo.type === 'roman') && !autoNumberSubitems) {
        console.log('[ClaimNumbering] Subnumber auto-numbering DISABLED, inserting plain newline');
        // Use proper indentation for non-numbered continuation: 7 spaces for level 1, 11 for level 2
        let properIndent = numberInfo.type === 'letter' ? '       ' : '           '; // 7 or 11 spaces
        const transaction = state.update({
            changes: {
                from: pos,
                to: pos,
                insert: '\n' + properIndent
            },
            selection: {
                anchor: pos + ('\n' + properIndent).length
            }
        });
        view.dispatch(transaction);
        return true;
    }
    
    // Determine next value
    let nextValue = getNextValue(numberInfo.type, numberInfo.value);
    
    // Format the next number
    let insertText = '\n' + numberInfo.indent;
    switch (numberInfo.type) {
        case 'number':
            insertText += `${nextValue}. `;
            break;
        case 'letter':
            insertText += `(${nextValue}) `;
            break;
        case 'roman':
            insertText += `(${nextValue}) `;
            break;
    }
    
    console.log('[ClaimNumbering] Next number will be:', insertText);
    
    // Insert newline with next number
    const transaction = state.update({
        changes: {
            from: pos,
            to: pos,
            insert: insertText
        },
        selection: {
            anchor: pos + insertText.length
        }
    });
    
    console.log('[ClaimNumbering] Dispatching transaction to insert:', insertText);
    view.dispatch(transaction);
    
    // Set up auto-capitalization only for main claim numbers
    if (numberInfo.type === 'number') {
        setupAutoCapitalization(view);
    }
    
    return true;
}

/**
 * Handle Tab key to indent and start subnumbering
 */
function handleTab(view) {
    const state = view.state;
    const selection = state.selection.main;
    
    if (!selection.empty) return false;
    
    const pos = selection.head;
    const line = state.doc.lineAt(pos);
    const numberInfo = detectNumberingLevel(line);
    
    console.log('[ClaimNumbering] Tab pressed:', {
        pos,
        lineText: line.text,
        numberInfo
    });
    
    // If we're on a numbered line (empty or with content), handle indentation
    if (numberInfo) {
        // Check settings for subnumber auto-numbering
        const settings = get(claimsSettings);
        const autoNumberSubitems = settings.autoNumberSubitems;
        
        console.log('[ClaimNumbering] Tab handler - checking settings:', {
            autoNumberSubitems,
            currentType: numberInfo.type,
            isEmpty: isEmptyNumberedLine(line)
        });
        
        let newNumber = '';
        let indent = '    '; // 4 spaces for first level
        let contentAfterNumber = '';
        
        // Extract content after the number if any
        if (!isEmptyNumberedLine(line)) {
            const afterPattern = line.text.substring(numberInfo.pattern.length);
            contentAfterNumber = afterPattern;
        }
        
        if (numberInfo.type === 'number') {
            // Convert main claim to letter subnumber
            if (autoNumberSubitems) {
                // Find the next appropriate letter based on existing letters in this section
                const nextLetter = findNextLetter(state, line.number);
                newNumber = indent + '(' + nextLetter + ') ' + contentAfterNumber;
            } else {
                // Just indent without adding subnumber - use 7 spaces for level 1
                newNumber = '       ' + contentAfterNumber;  // 7 spaces
            }
        } else if (numberInfo.type === 'letter') {
            // Convert letter to roman subnumber
            indent = '        '; // 8 spaces for second level
            if (autoNumberSubitems) {
                // Find the next appropriate roman numeral based on existing romans in this section
                const nextRoman = findNextRoman(state, line.number);
                newNumber = indent + '(' + nextRoman + ') ' + contentAfterNumber;
            } else {
                // Just indent without adding subnumber - use 11 spaces for level 2
                newNumber = '           ' + contentAfterNumber;  // 11 spaces
            }
        } else {
            // Already at deepest level (roman numerals), don't allow further indentation
            console.log('[ClaimNumbering] Already at maximum indent level (3), blocking further indentation');
            return true; // Return true to prevent default Tab behavior
        }
        
        // Calculate new cursor position - maintain relative position in content
        const relativePos = pos - (line.from + numberInfo.pattern.length);
        const newCursorPos = line.from + newNumber.length - contentAfterNumber.length + relativePos;
        
        // Replace the current line with the new indented version
        const transaction = state.update({
            changes: {
                from: line.from,
                to: line.to,
                insert: newNumber
            },
            selection: {
                anchor: Math.max(line.from, Math.min(newCursorPos, line.from + newNumber.length)),
                head: Math.max(line.from, Math.min(newCursorPos, line.from + newNumber.length))
            }
        });
        
        view.dispatch(transaction);
        // Don't auto-capitalize for subnumbers (only for main claims)
        return true;
    }
    
    // Check current indentation level to prevent going beyond 3 levels
    const currentIndent = line.text.match(/^(\s*)/)[1].length;
    // Check settings for subnumber auto-numbering
    const settings = get(claimsSettings);
    const autoNumberSubitems = settings.autoNumberSubitems;
    
    if (!autoNumberSubitems) {
        // When auto-numbering disabled, check for 7 and 11 space indents
        if (currentIndent >= 11) {
            // Already at or beyond level 2 (11 spaces), don't allow more
            console.log('[ClaimNumbering] Tab pressed at maximum indent (11 spaces), blocking');
            return true; // Block further indentation
        } else if (currentIndent >= 7) {
            // At level 1 (7 spaces), move to level 2 (11 spaces)
            const newIndent = '           '; // 11 spaces
            const restOfLine = line.text.substring(currentIndent);
            const transaction = state.update({
                changes: {
                    from: line.from,
                    to: line.to,
                    insert: newIndent + restOfLine
                },
                selection: {
                    anchor: pos + (11 - currentIndent)
                }
            });
            view.dispatch(transaction);
            return true;
        } else if (currentIndent >= 3) {
            // Near level 0, move to level 1 (7 spaces)
            const newIndent = '       '; // 7 spaces
            const restOfLine = line.text.substring(currentIndent);
            const transaction = state.update({
                changes: {
                    from: line.from,
                    to: line.to,
                    insert: newIndent + restOfLine
                },
                selection: {
                    anchor: pos + (7 - currentIndent)
                }
            });
            view.dispatch(transaction);
            return true;
        }
    } else {
        // Original behavior for when auto-numbering is enabled
        if (currentIndent >= 8) {
            // Already at or beyond second level indent, don't allow more
            console.log('[ClaimNumbering] Tab pressed at maximum indent, blocking');
            return true; // Block further indentation
        }
    }
    
    // Otherwise, perform normal indent
    return indentMore(view);
}

/**
 * Handle Shift+Tab to outdent and potentially move up numbering levels
 */
function handleShiftTab(view) {
    const state = view.state;
    const selection = state.selection.main;
    
    if (!selection.empty) return false;
    
    const pos = selection.head;
    const line = state.doc.lineAt(pos);
    const numberInfo = detectNumberingLevel(line);
    
    console.log('[ClaimNumbering] Shift+Tab pressed:', {
        pos,
        lineText: line.text,
        numberInfo
    });
    
    // If we're on a numbered line (empty or with content), convert to previous level
    if (numberInfo) {
        // Check settings for subnumber auto-numbering
        const settings = get(claimsSettings);
        const autoNumberSubitems = settings.autoNumberSubitems;
        
        let newNumber = '';
        let contentAfterNumber = '';
        
        // Extract content after the number if any
        if (!isEmptyNumberedLine(line)) {
            const afterPattern = line.text.substring(numberInfo.pattern.length);
            contentAfterNumber = afterPattern;
        }
        
        if (numberInfo.type === 'roman') {
            // Convert roman back to letter
            if (autoNumberSubitems) {
                // Find the next appropriate letter based on existing letters in this section
                const nextLetter = findNextLetter(state, line.number);
                newNumber = '    (' + nextLetter + ') ' + contentAfterNumber;
            } else {
                // Just outdent without numbering - use 7 spaces for level 1
                newNumber = '       ' + contentAfterNumber; // 7 spaces for level 1
            }
        } else if (numberInfo.type === 'letter') {
            // Convert letter back to main claim
            // Be smarter about finding the next main claim number
            const lastNumber = findLastNumber(state, 'number') || 0;
            const nextMainNumber = lastNumber + 1;
            newNumber = nextMainNumber + '. ' + contentAfterNumber;
        } else {
            // Already at top level, just outdent
            return indentLess(view);
        }
        
        // Calculate new cursor position - maintain relative position in content
        const relativePos = pos - (line.from + numberInfo.pattern.length);
        const newCursorPos = line.from + newNumber.length - contentAfterNumber.length + relativePos;
        
        // Replace the current number with the previous level
        const transaction = state.update({
            changes: {
                from: line.from,
                to: line.to,
                insert: newNumber
            },
            selection: {
                anchor: Math.max(line.from, Math.min(newCursorPos, line.from + newNumber.length)),
                head: Math.max(line.from, Math.min(newCursorPos, line.from + newNumber.length))
            }
        });
        
        view.dispatch(transaction);
        // Only auto-capitalize if we're going back to main claim level
        if (numberInfo.type === 'letter') {
            setupAutoCapitalization(view);
        }
        return true;
    }
    
    // Handle outdenting for non-numbered lines when auto-numbering is disabled
    const settings = get(claimsSettings);
    const autoNumberSubitems = settings.autoNumberSubitems;
    
    if (!autoNumberSubitems) {
        const currentIndent = line.text.match(/^(\s*)/)[1].length;
        
        if (currentIndent >= 11) {
            // At level 2 (11 spaces), move back to level 1 (7 spaces)
            const newIndent = '       '; // 7 spaces
            const restOfLine = line.text.substring(currentIndent);
            const transaction = state.update({
                changes: {
                    from: line.from,
                    to: line.to,
                    insert: newIndent + restOfLine
                },
                selection: {
                    anchor: pos - (currentIndent - 7)
                }
            });
            view.dispatch(transaction);
            return true;
        } else if (currentIndent >= 7) {
            // At level 1 (7 spaces), move back to level 0 
            // Generate next main claim number
            const lastNumber = findLastNumber(state, 'number') || 0;
            const nextMainNumber = lastNumber + 1;
            const restOfLine = line.text.substring(currentIndent);
            const newLine = nextMainNumber + '. ' + restOfLine;
            const transaction = state.update({
                changes: {
                    from: line.from,
                    to: line.to,
                    insert: newLine
                },
                selection: {
                    anchor: line.from + (nextMainNumber + '. ').length
                }
            });
            view.dispatch(transaction);
            // Set up auto-capitalization for main claim
            setupAutoCapitalization(view);
            return true;
        }
    }
    
    // Otherwise, perform normal outdent
    return indentLess(view);
}

/**
 * Set up auto-capitalization for the next character typed
 */
function setupAutoCapitalization(view) {
    let handled = false;
    
    console.log('[ClaimNumbering] Setting up auto-capitalization handler');
    
    const domHandler = (event) => {
        // Only handle regular character input
        if (handled || event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }
        
        console.log('[ClaimNumbering] Auto-cap keydown:', {
            key: event.key,
            isLetter: /[a-z]/i.test(event.key)
        });
        
        // Check if it's a letter
        if (event.key.length === 1 && /[a-z]/i.test(event.key)) {
            event.preventDefault();
            handled = true;
            
            // Insert capitalized character
            const capitalizedChar = event.key.toUpperCase();
            console.log('[ClaimNumbering] Auto-capitalizing:', event.key, '->', capitalizedChar);
            
            const transaction = view.state.update({
                changes: {
                    from: view.state.selection.main.head,
                    to: view.state.selection.main.head,
                    insert: capitalizedChar
                },
                selection: {
                    anchor: view.state.selection.main.head + 1
                }
            });
            
            view.dispatch(transaction);
            
            // Remove the handler after first character
            view.contentDOM.removeEventListener('keydown', domHandler, true);
        } else if (event.key !== 'Shift') {
            // Remove handler if non-letter key pressed (except Shift)
            console.log('[ClaimNumbering] Non-letter key pressed, removing handler');
            view.contentDOM.removeEventListener('keydown', domHandler, true);
        }
    };
    
    // Add event listener with capture to intercept before CodeMirror
    view.contentDOM.addEventListener('keydown', domHandler, true);
    
    // Also remove after a timeout in case user doesn't type
    setTimeout(() => {
        console.log('[ClaimNumbering] Auto-cap timeout reached, removing handler');
        view.contentDOM.removeEventListener('keydown', domHandler, true);
    }, 5000);
}

/**
 * Create the extension with highest priority
 */
export function claimNumberingExtension() {
    console.log('[ClaimNumbering] Creating extension');
    console.log('[ClaimNumbering] Current settings at extension creation:', get(claimsSettings));
    
    const keyMap = Prec.highest(keymap.of([
        {
            key: 'Tab',
            run: (view) => {
                // Check if autocomplete is active - if so, let it handle Tab
                const completion = completionStatus(view.state);
                if (completion === 'active') {
                    console.log('[ClaimNumbering] Tab pressed but autocomplete active, passing through');
                    return false;
                }
                return handleTab(view);
            }
        },
        {
            key: 'Shift-Tab',
            run: handleShiftTab
        },
        {
            key: 'Enter',
            run: (view) => {
                // Check if autocomplete is active - if so, let it handle Enter
                const completion = completionStatus(view.state);
                if (completion === 'active') {
                    console.log('[ClaimNumbering] Enter pressed but autocomplete active, passing through');
                    return false;
                }
                
                const state = view.state;
                const selection = state.selection.main;
                
                // Add debug logging
                console.log('[ClaimNumbering] Enter pressed', {
                    pos: selection.head,
                    empty: selection.empty,
                    lineText: state.doc.lineAt(selection.head).text
                });
                
                // Only handle when no text is selected
                if (!selection.empty) {
                    console.log('[ClaimNumbering] Selection not empty, skipping');
                    return false;
                }
                
                const pos = selection.head;
                const line = state.doc.lineAt(pos);
                
                // Case 1: Empty numbered line - remove number and exit
                if (isEmptyNumberedLine(line) && pos === line.to) {
                    console.log('[ClaimNumbering] Removing empty numbered line');
                    return removeNumbering(view, line);
                }
                
                // Case 2: End of numbered line - insert next number
                if (isAtEndOfNumberedLine(state, pos)) {
                    console.log('[ClaimNumbering] Inserting next number');
                    return insertNextNumber(view, pos);
                }
                
                // Case 3: Let default Enter handler take over
                console.log('[ClaimNumbering] Not a numbered context, passing through');
                return false;
            }
        }
    ]));
    
    console.log('[ClaimNumbering] Keymap extension created with highest priority:', keyMap);
    return keyMap;
}